import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdminSidebar } from "@/pages/AdminUsers";
import { UserPlus, Mail, Trash2, ChevronRight, CheckCircle2, Clock, Copy, Link2, Building2 } from "lucide-react";
import PublicSignupLinks from "@/components/PublicSignupLinks";

const STATUS_TONE = {
  sent:     { bg: "bg-[#D4AF37]/12", fg: "text-[#0B132B]", label: "Awaiting signature" },
  signed:   { bg: "bg-blue-500/12",  fg: "text-blue-700",  label: "Ready to countersign" },
  executed: { bg: "bg-emerald-500/12", fg: "text-emerald-700", label: "Executed" },
};

export default function AdminAgents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", legal_name: "", agency_name: "", custom_addendum: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/agents");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load agents");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitInvite = async () => {
    if (!invite.email || !invite.legal_name || !invite.agency_name) {
      toast.error("Email, legal name, and agency name are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/agents/invite", invite);
      toast.success("Invitation sent");
      setInvite({ email: "", legal_name: "", agency_name: "", custom_addendum: "" });
      setInviteOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to invite");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Cancel this pending agreement? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/agents/${id}`);
      toast.success("Cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Cancel failed");
    }
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/agents/${id}/resend-invite`);
      toast.success("Invitation email resent");
    } catch (e) {
      toast.error(formatApiError(e) || "Resend failed");
    }
  };

  const byStage = useMemo(() => {
    const map = Object.fromEntries((data?.stages || []).map((s) => [s.key, []]));
    (data?.items || []).forEach((it) => {
      const key = it.stage || "agreement_sent";
      if (!map[key]) map[key] = [];
      map[key].push(it);
    });
    return map;
  }, [data]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Administration
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">
              Agents
            </h1>
            <p className="text-sm text-[#6C757D] mt-3 max-w-xl leading-relaxed">
              Send Producer Agreements, countersign, and track new-agent onboarding.
            </p>
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white rounded-sm h-11 px-6"
                data-testid="admin-agents-invite-btn"
              >
                <UserPlus size={16} className="mr-2" /> Invite agent
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Send Producer Agreement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Agent email</Label>
                  <Input
                    type="email"
                    value={invite.email}
                    onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                    placeholder="agent@example.com"
                    data-testid="admin-agents-invite-email"
                  />
                </div>
                <div>
                  <Label>Legal name</Label>
                  <Input
                    value={invite.legal_name}
                    onChange={(e) => setInvite({ ...invite, legal_name: e.target.value })}
                    placeholder="Jane Doe"
                    data-testid="admin-agents-invite-name"
                  />
                </div>
                <div>
                  <Label>Agency name</Label>
                  <Input
                    value={invite.agency_name}
                    onChange={(e) => setInvite({ ...invite, agency_name: e.target.value })}
                    placeholder="Doe Warranty Partners LLC"
                    data-testid="admin-agents-invite-agency"
                  />
                </div>
                <div>
                  <Label>Custom addendum (optional)</Label>
                  <Textarea
                    rows={5}
                    value={invite.custom_addendum}
                    onChange={(e) => setInvite({ ...invite, custom_addendum: e.target.value })}
                    placeholder="Per-agent terms shown on the sign page above the master agreement."
                    data-testid="admin-agents-invite-addendum"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button
                  onClick={submitInvite}
                  disabled={saving}
                  className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white"
                  data-testid="admin-agents-invite-submit"
                >
                  <Mail size={14} className="mr-2" />
                  {saving ? "Sending…" : "Send agreement"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          <AdminSidebar />
          <div className="space-y-8">
            {/* Permanent public sign-up links — embed on the website, drop into
                marketing emails. Both flows land in the admin queue for
                countersign / packet completion. */}
            <PublicSignupLinks />

            {/* Pipeline board */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="admin-agents-pipeline">
              {(data?.stages || []).map((s) => (
                <Card key={s.key} className="bg-white border border-[#E9ECEF] rounded-sm p-5 min-h-[220px]" data-testid={`admin-agents-col-${s.key}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
                      {s.label}
                    </div>
                    <div className="text-xs font-mono text-[#6C757D]">
                      {(data?.counts || {})[s.key] || 0}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(byStage[s.key] || []).map((it) => (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => navigate(`/admin/agents/${it.id}`)}
                        className="w-full text-left border border-[#E9ECEF] hover:border-[#0B132B]/40 rounded-sm p-3 transition-colors"
                        data-testid={`admin-agents-card-${it.id}`}
                      >
                        <div className="font-semibold text-[#0B132B] text-sm truncate">
                          {it.invite_agency_name}
                        </div>
                        <div className="text-xs text-[#6C757D] truncate">
                          {it.invite_name} · {it.invite_email}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm ${STATUS_TONE[it.status]?.bg} ${STATUS_TONE[it.status]?.fg}`}>
                            {STATUS_TONE[it.status]?.label || it.status}
                          </span>
                        </div>
                      </button>
                    ))}
                    {(byStage[s.key] || []).length === 0 && !loading && (
                      <div className="text-xs text-[#6C757D] italic">Empty</div>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Full list */}
            <Card className="bg-white border border-[#E9ECEF] rounded-sm">
              <div className="p-6 border-b border-[#E9ECEF] flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                  All agents ({data?.items?.length || 0})
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-[0.16em] text-[#6C757D]">
                  <tr>
                    <th className="text-left p-4">Agency</th>
                    <th className="text-left p-4">Agent</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Stage</th>
                    <th className="text-left p-4">Created</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((it) => (
                    <tr key={it.id} className="border-t border-[#E9ECEF] hover:bg-[#F8F9FA]/50">
                      <td className="p-4 font-semibold text-[#0B132B]">{it.invite_agency_name}</td>
                      <td className="p-4">
                        <div className="text-[#0B132B]">{it.invite_name}</div>
                        <div className="text-xs text-[#6C757D]">{it.invite_email}</div>
                      </td>
                      <td className="p-4">
                        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm ${STATUS_TONE[it.status]?.bg} ${STATUS_TONE[it.status]?.fg}`}>
                          {STATUS_TONE[it.status]?.label || it.status}
                        </span>
                      </td>
                      <td className="p-4 text-[#6C757D]">
                        {(data?.stages || []).find((s) => s.key === it.stage)?.label || it.stage}
                      </td>
                      <td className="p-4 text-xs text-[#6C757D] font-mono">
                        {it.created_at ? new Date(it.created_at).toLocaleDateString() : ""}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {it.status !== "executed" && (
                            <>
                              <button
                                type="button"
                                onClick={() => resend(it.id)}
                                title="Resend invitation email"
                                className="p-2 hover:bg-[#0B132B]/5 rounded-sm text-[#6C757D] hover:text-[#0B132B]"
                                data-testid={`admin-agents-resend-${it.id}`}
                              >
                                <Mail size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => remove(it.id)}
                                title="Cancel"
                                className="p-2 hover:bg-red-50 rounded-sm text-[#6C757D] hover:text-red-600"
                                data-testid={`admin-agents-delete-${it.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                          <Link
                            to={`/admin/agents/${it.id}`}
                            className="p-2 hover:bg-[#0B132B]/5 rounded-sm text-[#6C757D] hover:text-[#0B132B]"
                            data-testid={`admin-agents-open-${it.id}`}
                          >
                            <ChevronRight size={14} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(data?.items || []).length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-[#6C757D] italic">
                        No agent agreements yet. Click <span className="font-semibold text-[#0B132B]">Invite agent</span> to send the first one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
